import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Share2, Sparkles, User, MessageCircle, Instagram, Mail, Copy, Check, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { Button } from '../../ui/button';

// V1.2 — Icônes SVG inline pour WhatsApp et TikTok (pas dans lucide)
const WhatsAppIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const TikTokIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const ReferralTab = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        // Nouvel endpoint prioritaire
        const res = await axios.get(`${API}/referral/my-stats/${user?.user_id || user?.id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        setStats(res.data);
      } catch (e) {
        // Fallback sur l'ancien endpoint
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/referrals/stats`, {
            headers: { 'Authorization': `Bearer ${token}` },
            withCredentials: true
          });
          setStats(res.data);
        } catch {}
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchStats();
  }, [user]);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success('Copié !');
    setTimeout(() => setCopied(''), 2000);
  };

  // V1.2 — Code parrainage du user
  const code = stats?.referral_code || user?.referral_code || '...';

  // V1.2 — Lien dynamique basé sur le vrai domaine courant (pas le preview Emergent)
  const siteOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://effaceurpro.fr';
  const referralLink = `${siteOrigin}/register?ref=${code}`;

  // V1.2 — Credits réels (système promo_codes unifié — crédit direct à l'utilisation)
  const totalFilleuls = stats?.total_referrals ?? stats?.completed ?? 0;
  const creditsTotal = stats?.paid_rewards ?? stats?.earnings ?? (user?.referral_credits || 0);

  // V1.2 — Messages prêts à partager, alignés avec les nouveaux packs Déménagement / Héritage
  const messages = {
    sms: `Déménagement à venir ? Succession à gérer ? J'utilise L'EFFACEUR pour résilier tous les contrats en 1 clic. Avec mon code ${code}, tu as -10% : ${referralLink}`,
    whatsapp: `Salut ! 👋 Je te recommande L'EFFACEUR pour gérer la résiliation de tes contrats (déménagement / succession). Tout est automatisé, sans paperasse. Avec mon code ${code} tu bénéficies de -10%. Voici le lien : ${referralLink}`,
    email_subject: `L'EFFACEUR — J'ai -10% pour toi`,
    email_body: `Bonjour,

Je voulais te partager un service que j'utilise personnellement : L'EFFACEUR.

C'est un service qui résilie automatiquement tous tes contrats (électricité, internet, assurances, banque…) lors d'un déménagement ou d'une succession. Un seul mandat, tout est géré à ta place.

Avec mon code parrainage ${code}, tu obtiens -10% sur ton pack :
${referralLink}

(Et moi je gagne 10€ de crédit quand tu prends un pack — gagnant-gagnant.)

À bientôt !`,
    insta: `Marre de la paperasse quand tu déménages ou que tu gères une succession ? 😮‍💨 Je teste @leffaceur et franchement, un mandat, zéro démarche. Avec mon code ${code} c'est -10%. 🔗 ${referralLink} #LEffaceur #Toulouse #demenagement`,
    tiktok: `POV : tu déménages et t'as 15 contrats à résilier un par un 💀 Moi j'ai pris L'EFFACEUR, je signe 1 mandat, ils font tout. Code ${code} = -10%. 🔗 ${referralLink} #leffaceur #demenagement #fyp`
  };

  const shareViaSMS = () => window.open(`sms:?body=${encodeURIComponent(messages.sms)}`, '_blank');
  const shareViaWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(messages.whatsapp)}`, '_blank');
  const shareViaEmail = () => window.open(`mailto:?subject=${encodeURIComponent(messages.email_subject)}&body=${encodeURIComponent(messages.email_body)}`, '_blank');
  const copyInsta = () => { navigator.clipboard.writeText(messages.insta); toast.success('Message Instagram copié'); setCopied('insta'); setTimeout(() => setCopied(''), 2000); };
  const copyTikTok = () => { navigator.clipboard.writeText(messages.tiktok); toast.success('Message TikTok copié'); setCopied('tiktok'); setTimeout(() => setCopied(''), 2000); };

  // Partage natif mobile (Web Share API)
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'L\'EFFACEUR — -10% avec mon code',
          text: messages.sms,
          url: referralLink
        });
      } catch {}
    } else {
      copyText(referralLink, 'link');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Gift className="w-6 h-6 text-magenta-400" />Parrainage : offrez -10%, gagnez 10€
        </h2>
        <p className="text-gray-400 font-mono text-sm">// PROGRAMME_PARRAINAGE · WIN_WIN</p>
      </div>

      {/* Carte principale — le deal */}
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="glass-card p-6 md:p-8 border-2 border-magenta-400 bg-gradient-to-br from-magenta-400/10 to-cyan-400/5">
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <div>
            <Sparkles className="w-10 h-10 text-magenta-400 mb-3" />
            <h3 className="font-orbitron text-xl md:text-2xl font-bold text-white mb-3">Un partage, deux gagnants</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-cyan-400 font-bold mt-0.5">→</span>
                <p className="text-gray-300">Votre filleul obtient <span className="text-cyan-400 font-bold">-10%</span> sur son pack Déménagement ou Héritage.</p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-magenta-400 font-bold mt-0.5">→</span>
                <p className="text-gray-300">Vous recevez <span className="text-success font-bold">10€ de crédit</span> dès qu'il achète, utilisable sur votre prochain pack.</p>
              </div>
            </div>
          </div>
          <div className="bg-void/60 border border-magenta-400/30 rounded-lg p-4 text-center">
            <p className="text-gray-400 text-xs font-mono uppercase tracking-widest mb-2">Votre code</p>
            <p className="font-orbitron text-3xl md:text-4xl font-black text-magenta-400 tracking-wider mb-3" style={{ textShadow: '0 0 20px rgba(255,0,255,0.5)' }}>
              {code}
            </p>
            <Button onClick={() => copyText(code, 'code')} className="bg-magenta-400 text-black hover:bg-magenta-300 font-orbitron text-xs">
              {copied === 'code' ? <><Check className="w-4 h-4 mr-2" />Copié</> : <><Copy className="w-4 h-4 mr-2" />Copier le code</>}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Partage rapide — 5 boutons en row */}
      <div className="glass-card p-5 border border-gray-800">
        <h3 className="font-orbitron text-lg font-bold text-white mb-1">Partager en un clic</h3>
        <p className="text-gray-500 text-xs font-mono mb-4">// CHOISISSEZ_VOTRE_CANAL</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Button onClick={shareViaSMS} className="bg-blue-500 hover:bg-blue-600 text-white font-orbitron h-14 flex-col gap-1" data-testid="share-sms">
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs">SMS</span>
          </Button>
          <Button onClick={shareViaWhatsApp} className="bg-green-500 hover:bg-green-600 text-white font-orbitron h-14 flex-col gap-1" data-testid="share-whatsapp">
            <WhatsAppIcon className="w-5 h-5" />
            <span className="text-xs">WhatsApp</span>
          </Button>
          <Button onClick={shareViaEmail} className="bg-gray-600 hover:bg-gray-700 text-white font-orbitron h-14 flex-col gap-1" data-testid="share-email">
            <Mail className="w-5 h-5" />
            <span className="text-xs">Email</span>
          </Button>
          <Button onClick={copyInsta} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:brightness-110 text-white font-orbitron h-14 flex-col gap-1" data-testid="share-insta">
            <Instagram className="w-5 h-5" />
            <span className="text-xs">Instagram</span>
          </Button>
          <Button onClick={copyTikTok} className="bg-black border border-gray-700 hover:bg-gray-900 text-white font-orbitron h-14 flex-col gap-1" data-testid="share-tiktok">
            <TikTokIcon className="w-5 h-5" />
            <span className="text-xs">TikTok</span>
          </Button>
        </div>
        {/* Partage natif mobile */}
        <Button onClick={nativeShare} variant="outline" className="w-full mt-3 border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/10 md:hidden">
          <Send className="w-4 h-4 mr-2" />Partager via mon téléphone
        </Button>
      </div>

      {/* Lien complet */}
      <div className="glass-card p-4 border border-cyan-400/20 bg-cyan-400/5">
        <p className="text-gray-500 text-xs font-mono mb-2">// LIEN_DIRECT</p>
        <div className="flex flex-col md:flex-row gap-2">
          <div className="flex-1 bg-gray-900/60 border border-gray-700 rounded-lg p-3 font-mono text-xs md:text-sm text-cyan-400 truncate">
            {referralLink}
          </div>
          <Button onClick={() => copyText(referralLink, 'link')} variant="outline" className="border-cyan-400 text-cyan-400 hover:bg-cyan-400/10">
            {copied === 'link' ? <><Check className="w-4 h-4 mr-2" />Copié</> : <><Copy className="w-4 h-4 mr-2" />Copier le lien</>}
          </Button>
        </div>
      </div>

      {/* Stats — notre système crédite directement, plus de "pending" */}
      <div className="grid grid-cols-2 gap-4">
        <div className="glass-card p-5 border border-gray-800 text-center">
          <p className="font-orbitron text-4xl font-black text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,245,255,0.3)' }}>
            {totalFilleuls}
          </p>
          <p className="text-gray-400 text-sm mt-2 font-mono uppercase tracking-wider">
            {totalFilleuls <= 1 ? 'Filleul' : 'Filleuls'}
          </p>
        </div>
        <div className="glass-card p-5 border border-gray-800 text-center">
          <p className="font-orbitron text-4xl font-black text-success" style={{ textShadow: '0 0 20px rgba(0,255,148,0.3)' }}>
            {creditsTotal}€
          </p>
          <p className="text-gray-400 text-sm mt-2 font-mono uppercase tracking-wider">Crédits gagnés</p>
        </div>
      </div>

      {/* Liste des filleuls si présente */}
      {stats?.filleuls && stats.filleuls.length > 0 && (
        <div className="glass-card p-4 border border-gray-800">
          <h3 className="font-orbitron text-base font-bold text-white mb-3">Vos filleuls</h3>
          <div className="space-y-2">
            {stats.filleuls.map((f, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-900/50 p-3 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-cyan-400/20 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span className="text-gray-300 font-mono text-sm">{f.filleul_email || f.email || '—'}</span>
                </div>
                <span className="text-success font-mono font-bold">+{f.reward_amount || 10}€</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aperçu du message qui sera envoyé */}
      <details className="glass-card p-4 border border-gray-800">
        <summary className="cursor-pointer font-orbitron text-sm text-gray-400 hover:text-white">
          // Voir l'aperçu du message partagé
        </summary>
        <div className="mt-3 bg-void/60 border-l-2 border-cyan-400 p-4 text-gray-300 text-sm leading-relaxed">
          {messages.sms}
        </div>
      </details>
    </div>
  );
};

export { ReferralTab };
